import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, RotateCcw, AlertTriangle, Info, BookOpen, Scale, Eye, Beaker } from 'lucide-react';

/**
 * L'esito non è una prescrizione.
 *
 * Mostra dove UMIFUL si colloca rispetto al caso che il professionista ha in
 * mano: le aree coinvolte, cosa documenta la letteratura, cosa autorizza
 * l'etichetta, cosa riporta chi lo impiega e le cose da considerare.
 * La decisione resta a chi ha il paziente davanti.
 */
export default function WizardResult({ result, onReset }) {
  const navigate = useNavigate();
  const {
    age_group, selected_areas = [], etichetta, osservazioni = [],
    modo_uso, considerazioni = [],
  } = result;

  const primaryArea = selected_areas[0];

  return (
    <div className="space-y-4" data-testid="wizard-result">
      {/* Intestazione */}
      <Card className="rounded-2xl border-none shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-[#12100c] to-[#1a1712] p-5 sm:p-6">
          <Badge className="bg-white/15 text-white border-none text-[10px] font-semibold tracking-wide uppercase mb-3">
            Dove si colloca
          </Badge>
          <h2 className="text-white font-heading text-xl sm:text-2xl font-bold mb-2" data-testid="result-title">
            Il caso che hai descritto tocca queste aree
          </h2>
          <p className="text-white/70 text-sm leading-relaxed mb-3">
            {age_group?.label ? `${age_group.label}. ` : ''}
            Qui sotto trovi cosa documenta la letteratura, cosa si può dichiarare e cosa
            riporta chi lo impiega. La valutazione resta tua.
          </p>
          <div className="flex flex-wrap gap-2">
            {selected_areas.map(a => (
              <Badge key={a.slug} className="bg-white/20 text-white border-none text-xs">
                {a.icon} {a.title}
              </Badge>
            ))}
          </div>
        </div>
      </Card>

      {selected_areas.length === 0 && (
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardContent className="p-6 text-sm text-[#4a4238]">
            Per questa fascia di età le aree selezionate non sono pertinenti. Prova a ricominciare
            indicando un altro quadro.
          </CardContent>
        </Card>
      )}

      {/* Cosa documenta la letteratura */}
      {selected_areas.map(a => (
        <Card key={a.slug} className="rounded-2xl border-gray-100 shadow-sm" data-testid={`result-area-${a.slug}`}>
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">{a.icon}</span>
              <h3 className="font-heading text-base font-semibold text-[#12100c]">{a.title}</h3>
            </div>
            {a.sintesi && (
              <p className="text-sm text-[#12100c] leading-relaxed mb-3">
                In quest'area il razionale d'impiego è {a.sintesi}.
              </p>
            )}
            {a.letteratura && (
              <div className="mb-3">
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#8a6620] mb-1.5 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5" /> Cosa copre la letteratura
                </p>
                <p className="text-sm text-[#4a4238] leading-relaxed">{a.letteratura}</p>
              </div>
            )}
            {a.quando_si_incontra?.length > 0 && (
              <div className="mb-3">
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#8a6620] mb-1.5">
                  Quadri in cui viene impiegato
                </p>
                <ul className="space-y-1">
                  {a.quando_si_incontra.map((q, i) => (
                    <li key={i} className="text-sm text-[#4a4238] flex gap-2">
                      <span className="text-[#8a6620] mt-0.5">&#8226;</span><span>{q}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {a.synergies && (
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#8a6620] mb-1.5 flex items-center gap-1.5">
                  <Beaker className="w-3.5 h-3.5" /> Con cosa si combina
                </p>
                <p className="text-sm text-[#4a4238] leading-relaxed">{a.synergies}</p>
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Cosa autorizza l'etichetta */}
      {etichetta && (
        <Card className="rounded-2xl border-[#c6a15b]/30 shadow-sm" data-testid="result-etichetta">
          <CardContent className="p-5 sm:p-6 bg-[#c6a15b]/[0.06]">
            <div className="flex items-center gap-2 mb-2">
              <Scale className="w-4 h-4 text-[#8a6620]" />
              <h3 className="font-semibold text-sm text-[#12100c]">{etichetta.titolo}</h3>
            </div>
            <p className="text-sm text-[#4a4238] leading-relaxed">{etichetta.testo}</p>
            {etichetta.fuori?.length > 0 && (
              <ul className="mt-3 space-y-1">
                {etichetta.fuori.map((f, i) => (
                  <li key={i} className="text-sm text-[#4a4238] flex gap-2">
                    <span className="text-[#8a6620] mt-0.5">&#8226;</span><span>{f}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cosa riporta chi lo impiega */}
      {osservazioni.length > 0 && (
        <Card className="rounded-2xl border-gray-100 shadow-sm" data-testid="result-osservazioni">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-1">
              <Eye className="w-4 h-4 text-[#4a5f43]" />
              <h3 className="font-semibold text-sm text-[#12100c]">Cosa riporta chi lo impiega</h3>
            </div>
            <p className="text-xs text-[#5b5346] mb-3">
              Tempi di osservazione riferiti da chi lo usa nella pratica. Non sono un risultato
              atteso né una previsione.
            </p>
            <div className="space-y-2">
              {osservazioni.map((o, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <span className="text-[#5b5346] shrink-0 w-32">{o.finestra}</span>
                  <span className="text-[#4a4238]">{o.cosa}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modo d'uso */}
      {modo_uso && (
        <Card className="rounded-2xl border-gray-100 shadow-sm" data-testid="result-modo-uso">
          <CardContent className="p-5 sm:p-6">
            <h3 className="font-semibold text-sm text-[#12100c] mb-1">Modo d'uso più diffuso</h3>
            <p className="text-xs text-[#5b5346] mb-3">
              Come viene assunto di norma nell'ottica che hai indicato. Dose e durata le stabilisci tu.
            </p>
            <div className="space-y-2 text-sm text-[#4a4238]">
              <div className="flex gap-2"><span className="font-medium text-[#12100c] shrink-0">Dose:</span> {modo_uso.dosage}</div>
              <div className="flex gap-2"><span className="font-medium text-[#12100c] shrink-0">Momento:</span> {modo_uso.timing}</div>
              <div className="flex gap-2"><span className="font-medium text-[#12100c] shrink-0">Durata:</span> {modo_uso.duration}</div>
              <div className="flex gap-2"><span className="font-medium text-[#12100c] shrink-0">Prime settimane:</span> {modo_uso.first_phase}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Da considerare */}
      {considerazioni.length > 0 && (
        <div className="space-y-2" data-testid="result-considerazioni">
          <h3 className="font-semibold text-sm text-[#12100c] pt-2">Cose da considerare</h3>
          {considerazioni.map((c, i) => (
            <div key={i} className={`rounded-xl p-4 flex gap-3 items-start bg-[#efe8da] border-l-2 ${
              c.tipo === 'danger' ? 'border-[#a33a3a]' :
              c.tipo === 'warning' ? 'border-[#a07c34]' :
              'border-[#8a6620]'
            }`}>
              {c.tipo === 'info'
                ? <Info className="w-5 h-5 text-[#8a6620] shrink-0 mt-0.5" />
                : <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${c.tipo === 'danger' ? 'text-[#a33a3a]' : 'text-[#a07c34]'}`} />}
              <p className={`text-sm leading-relaxed ${c.tipo === 'danger' ? 'text-[#7a2b2b]' : 'text-[#4a4238]'}`}>
                {c.testo}
              </p>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-[#5b5346] leading-relaxed pt-1">
        UMIFUL è un integratore alimentare. Quello che leggi qui serve a inquadrare il prodotto,
        non a sostituire la tua valutazione clinica.
      </p>

      {/* Azioni */}
      <div className="flex flex-col sm:flex-row gap-3 pt-2" data-testid="result-actions">
        {primaryArea && (
          <Button onClick={() => navigate(`/aree-applicazione#${primaryArea.slug}`)}
            className="bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full flex-1" data-testid="result-deep-dive-btn">
            Approfondisci quest'area <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
        <Button variant="outline" onClick={() => navigate('/racconta-un-caso')}
          className="rounded-full border-[#5f7d52] text-[#4a5f43] hover:bg-[#5f7d52] hover:text-white"
          data-testid="result-caso-btn">
          Racconta com'è andata
        </Button>
        <Button variant="outline" onClick={onReset}
          className="rounded-full border-[#12100c] text-[#12100c] hover:bg-[#12100c] hover:text-white" data-testid="result-restart-btn">
          <RotateCcw className="w-4 h-4 mr-2" /> Ricomincia
        </Button>
      </div>
    </div>
  );
}
